import { format, parseISO, subDays } from "date-fns";

import proxy from "api/proxy";
import { getStorage } from "store/local";

const calendarCache = new Map();

function toIsoDate(value) {
  if (value instanceof Date) return format(value, "yyyy-MM-dd");
  return String(value).slice(0, 10);
}

function getLastNight(endDate) {
  return format(subDays(parseISO(toIsoDate(endDate)), 1), "yyyy-MM-dd");
}

function getId(value) {
  return value?.id ?? value ?? null;
}

function getRatePlanId(parentRec) {
  return getId(parentRec?.rate_plan) ?? getId(parentRec?.channel?.rate_plan);
}

function getShopId(parentRec) {
  const session = getStorage("ctxSession");
  return getId(parentRec?.environment) ?? session?.shop ?? null;
}

async function getShopOc(shopId) {
  if (!shopId) return null;
  const { data } = await proxy.search(
    "sale.shop",
    [["id", "=", shopId]],
    ["operation_center"],
    1,
  );
  return getId(data?.[0]?.operation_center);
}

async function getCalendarOc(ratePlanId, shopId) {
  const key = `${ratePlanId}-${shopId}`;
  if (calendarCache.has(key)) return calendarCache.get(key);
  const { data } = await proxy.search(
    "hotel.calendar",
    [["rate_plan", "=", ratePlanId]],
    ["operation_center"],
  );
  let oc = null;
  if (data?.length) {
    const ocs = data.map((cal) => getId(cal.operation_center));
    const shopOc = ocs.length > 1 ? await getShopOc(shopId) : null;
    oc = ocs.includes(shopOc) ? shopOc : ocs[0];
  }
  calendarCache.set(key, oc);
  return oc;
}

async function getDapArgs(parentRec, startDate, endDate, priceListId) {
  const shop = getShopId(parentRec);
  const ratePlanId = getRatePlanId(parentRec);
  const args = {
    start_date: startDate,
    end_date: endDate,
    shop,
    price_list: priceListId ?? null,
    oc: null,
  };
  if (!ratePlanId) return args;
  args.rate_plan = ratePlanId;
  args.oc = await getCalendarOc(ratePlanId, shop);
  return args;
}

async function callDap(args) {
  const { data } = await proxy.methodCall({
    model: "hotel.room",
    method: "dap",
    args: [args],
    kwargs: {},
  });
  return data;
}

function mergeAvailability(data, nightData) {
  const byId = new Map(nightData.records.map((rec) => [rec.id, rec]));
  data.records.forEach(function applyNight(rec) {
    const night = byId.get(rec.id);
    if (!night) return;
    rec.availability = night.availability;
    rec.rooms = night.availability < 1 ? [] : night.rooms;
  });
  return data;
}

async function fetchAvailability({
  parentRec,
  startDate,
  endDate,
  priceListId,
}) {
  if (!startDate || !endDate) return null;
  const args = await getDapArgs(parentRec, startDate, endDate, priceListId);
  const data = await callDap(args);
  if (data?.status === "error" || !data?.records) return data;
  const nightArgs = { ...args, end_date: getLastNight(endDate) };
  const nightData = await callDap(nightArgs);
  if (nightData?.message === "minimum_stay_not_valid") return nightData;
  if (nightData?.status === "error" || !nightData?.records) return data;
  return mergeAvailability(data, nightData);
}

export { fetchAvailability };
